import type { OrderDetailLine } from '../api/orders';

/**
 * 挑拣清单的一行（§6.3 的 class 逐字照抄）。
 *
 * 关键点：**完成态只由 `line.done` 决定**（服务端下发，§4.5 的单一出处）。
 * 这个组件里没有 `quantity` 参与的判断 —— `quantity` 只用于显示与「全部挑完」的目标值。
 */
interface Props {
  line: OrderDetailLine;
  busy: boolean;
  onPick: (line: OrderDetailLine, nextPickedQty: number) => void;
  onPickAll: (line: OrderDetailLine) => void;
  onShort: (line: OrderDetailLine) => void;
}

export default function PickLineRow({ line, busy, onPick, onPickAll, onShort }: Props) {
  const done = line.done;
  /** 可挑上限 = 数量 − 已缺货（缺货占掉的数量不能再挑，否则服务端 400 `INVALID_QUANTITY`） */
  const pickable = Math.max(0, line.quantity - line.shortQty);

  /** 点整行/勾选框：未完成 → 累计 +1；已完成 → 撤销挑拣（缺货数不动，它单调不减） */
  const toggle = (): void => {
    if (busy) return;
    if (!done) {
      onPick(line, Math.min(pickable, line.pickedQty + 1));
      return;
    }
    if (line.pickedQty > 0) onPick(line, 0);
  };

  return (
    <div
      data-testid="pick-line"
      data-line-id={line.id}
      role="group"
      aria-label={`${line.productName}，货架 ${line.shelf?.code ?? '-'}，数量 ${line.quantity}`}
      onClick={toggle}
      className={`flex items-center gap-3 rounded-lg border-2 border-black p-3 ${
        done ? 'bg-gray-100' : 'bg-white'
      }`}
    >
      <span
        data-testid="pick-checkbox"
        role="checkbox"
        aria-checked={done}
        tabIndex={0}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-2 border-black text-lg font-bold ${
          done ? 'bg-black text-white' : 'bg-white active:bg-gray-100'
        }`}
      >
        {done ? '✓' : ''}
      </span>

      {/* 方案行的左侧蓝条（§6.9 ④）：只是**来源标记**，不改变数量与勾选交互 */}
      {line.lineSource === 'PROMOTION' && <span className="w-1 self-stretch rounded-full bg-blue-700" />}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-base font-bold ${done ? 'line-through text-gray-500' : ''}`}>
            {line.productName}
          </span>
          {line.lineSource === 'PROMOTION' && (
            <span
              data-testid="scheme-badge"
              className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700"
            >
              {line.schemeTitle ?? '组合促销'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {/* 货架位：单格 `A3`、合并区域 `A1-B2`，一律来自服务端快照（§7.8） */}
          <span data-testid="line-shelf">货架 {line.shelf?.code ?? '-'}</span>
          <span>数量 {line.quantity}</span>
          <span>已挑 {line.pickedQty}</span>
          {line.shortQty > 0 && <span className="text-red-600">缺货 {line.shortQty}</span>}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-1">
        <button
          type="button"
          data-testid="pick-all"
          disabled={busy || done || line.pickedQty >= pickable}
          onClick={(event) => {
            event.stopPropagation();
            onPickAll(line);
          }}
          className="flex h-8 items-center justify-center rounded-lg border-2 border-black px-2 text-xs font-bold active:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400"
        >
          全部挑完
        </button>
        {!done && (
          <button
            type="button"
            data-testid="short-button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onShort(line);
            }}
            className="flex h-8 items-center justify-center rounded-lg border-2 border-red-500 px-2 text-xs font-bold text-red-500 active:bg-red-50 disabled:bg-gray-200 disabled:text-gray-400"
          >
            缺货
          </button>
        )}
      </div>
    </div>
  );
}
